/**
 * Client-side rate limiter using localStorage
 * Prevents excessive form submissions from the same browser
 */

const RATE_LIMIT_KEY = 'contact_form_submissions';
const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hour in milliseconds
const MAX_SUBMISSIONS = 3; // Max 3 submissions per hour

/**
 * Check if user can submit the form
 * @returns {Object} { allowed: boolean, remainingTime: number }
 */
export const checkRateLimit = () => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    
    if (!stored) {
      return { allowed: true, remainingTime: 0 };
    }

    const submissions = JSON.parse(stored);
    const now = Date.now();
    
    // Filter out submissions older than the rate limit window
    const recentSubmissions = submissions.filter(
      timestamp => now - timestamp < RATE_LIMIT_WINDOW
    );

    if (recentSubmissions.length >= MAX_SUBMISSIONS) {
      const oldestSubmission = Math.min(...recentSubmissions);
      const remainingTime = RATE_LIMIT_WINDOW - (now - oldestSubmission);
      return { 
        allowed: false, 
        remainingTime: Math.ceil(remainingTime / 1000 / 60) // Convert to minutes
      };
    }

    // Update localStorage with filtered submissions
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
    
    return { allowed: true, remainingTime: 0 };
  } catch (error) {
    console.error('Rate limit check error:', error);
    // If there's an error, allow submission (fail open)
    return { allowed: true, remainingTime: 0 };
  }
};

/**
 * Record a form submission
 */
export const recordSubmission = () => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions = stored ? JSON.parse(stored) : [];
    const now = Date.now();
    
    // Filter out old submissions and add new one
    const recentSubmissions = submissions.filter(
      timestamp => now - timestamp < RATE_LIMIT_WINDOW
    );
    recentSubmissions.push(now);
    
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
  } catch (error) {
    console.error('Rate limit record error:', error);
  }
};

/**
 * Get remaining time in minutes until next submission is allowed
 * @returns {number} Minutes until next submission allowed
 */
export const getRemainingTime = () => {
  const { allowed, remainingTime } = checkRateLimit();
  return allowed ? 0 : remainingTime;
};

